import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import type { CarCatalogEntry } from '@parkproof/shared';
import { Pill } from './Pill';
import { Button } from './Button';
import { StatPair } from './StatBlock';
import { VerdictBox } from './VerdictBox';
import { LoadingState, ErrorState } from './LoadingState';
import { NavigateSheet } from './NavigateSheet';
import { CarPickerDialog } from './CarPickerDialog';
import { VerifyHeightDialog } from './VerifyHeightDialog';
import { ChevronLeft, Share, Navigate, Check } from './Icon';
import { useParking } from '../hooks/useParkings';
import { useAuth } from '../contexts/AuthContext';
import { useLoadout } from '../contexts/LoadoutContext';
import { submitReport } from '../services/reports';
import { parkingVerdict } from '../services/verdict';
import { bestHeightM } from '../types';
import type { Car } from '../types';

const newCarId = () => `car_${Date.now().toString(36)}`;

interface ParkingDetailSheetProps {
  parkingId: string;
  onClose: () => void;
}

/**
 * Sheet that slides over the map when the URL matches /parking/:id (Q20).
 * Content mirrors the retired DetailPage.tsx (name/address/verdict/actions)
 * in a sheet shell instead of a full page.
 */
export const ParkingDetailSheet: React.FC<ParkingDetailSheetProps> = ({ parkingId, onClose }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: parking, isLoading, isError, error } = useParking(parkingId);
  const { effectiveCars, firebaseUser, profile, saveCars } = useAuth();
  const { effectiveHeight, extraHeightM } = useLoadout();
  const car = effectiveCars[0];
  const totalHeight = effectiveHeight(car);

  const [tested, setTested] = useState(false);
  const [submittingTested, setSubmittingTested] = useState(false);
  const [navigateOpen, setNavigateOpen] = useState(false);
  const [carPickerOpen, setCarPickerOpen] = useState(false);
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [verifyTab, setVerifyTab] = useState<'manual' | 'photo'>('manual');
  const [actionError, setActionError] = useState<string | null>(null);

  const canVerify = !!firebaseUser && !firebaseUser.isAnonymous;

  const openVerify = (tab: 'manual' | 'photo') => {
    setVerifyTab(tab);
    setVerifyOpen(true);
  };

  const hasOwnCar = (profile?.cars?.length ?? 0) > 0;

  const handleShare = async () => {
    if (!parking) return;
    const heightM = bestHeightM(parking);
    const heightStr = heightM != null ? `${heightM.toFixed(2)} m clearance` : 'height unverified';
    const where = parking.address ?? `${parking.lat.toFixed(4)}, ${parking.lng.toFixed(4)}`;
    const message = `I'm parked at ${parking.name}, ${where}. ParkProof · ${heightStr}.`;
    if (navigator.share) {
      try {
        await navigator.share({ title: parking.name, text: message });
      } catch {
        /* user cancelled */
      }
    } else {
      try {
        await navigator.clipboard.writeText(message);
        setActionError('Link copied to clipboard.');
        setTimeout(() => setActionError(null), 2500);
      } catch {
        setActionError('Could not copy link.');
      }
    }
  };

  const handleTestedThis = async () => {
    if (!firebaseUser) return;
    if (tested || submittingTested) return;
    setSubmittingTested(true);
    setActionError(null);
    try {
      await submitReport({ parkingId, userId: firebaseUser.uid, reportType: 'tested_ok' });
      setTested(true);
    } catch (err) {
      setActionError((err as Error).message);
    } finally {
      setSubmittingTested(false);
    }
  };

  const handlePickCar = async (entry: CarCatalogEntry) => {
    setCarPickerOpen(false);
    const newCar: Car = {
      id: newCarId(),
      label: `${entry.make} ${entry.model}`,
      type: entry.type,
      heightM: entry.heightM,
    };
    try {
      await saveCars([...(profile?.cars ?? []), newCar]);
    } catch (err) {
      setActionError((err as Error).message);
    }
  };

  if (isLoading) {
    return (
      <SheetShell onClose={onClose}>
        <LoadingState label="Loading parking…" />
      </SheetShell>
    );
  }
  if (isError || !parking) {
    return (
      <SheetShell onClose={onClose}>
        <ErrorState message={(error as Error)?.message ?? 'Parking not found.'} />
      </SheetShell>
    );
  }

  const verdict = parkingVerdict(parking, totalHeight);
  const heightM = bestHeightM(parking);

  return (
    <SheetShell onClose={onClose} verdictPill={<Pill tier={verdict.tierColor} label={verdict.badge} solid />}>
      <div className="p-s5 flex flex-col gap-s5">
        <div>
          <h1 className="font-serif font-medium text-[24px] leading-[28px] tracking-tight text-ink1">
            {parking.name}
          </h1>
          <div className="text-[13px] text-ink3 font-sans mt-1">
            {parking.address ?? `${parking.lat.toFixed(4)}, ${parking.lng.toFixed(4)}`}
          </div>
        </div>

        <VerdictBox tier={verdict.tierColor} headline={verdict.headline} sub={verdict.sub} />

        {!hasOwnCar ? (
          <button
            type="button"
            onClick={() => setCarPickerOpen(true)}
            className="p-s4 bg-tint rounded-lg text-left"
          >
            <div className="text-[13px] font-sans font-semibold text-ink1 mb-0.5">
              Add your car to see if it fits
            </div>
            <div className="text-[12px] text-ink3 font-sans">
              Showing the default car ({car.label}) — pick yours for an accurate verdict
            </div>
          </button>
        ) : null}

        {heightM != null ? (
          <StatPair
            left={{
              label: 'Height limit',
              value: heightM.toFixed(2),
              unit: 'm',
              meta: parking.confidenceTier === 'verified' ? 'Confirmed by drivers' : 'Verify at entrance',
            }}
            right={{
              label: 'Your car',
              value: totalHeight.toFixed(2),
              unit: 'm',
              meta: extraHeightM > 0 ? `${car.label} + loadout` : car.label,
            }}
          />
        ) : null}

        <div className="text-[11px] text-ink3 font-mono uppercase tracking-wide">
          {parking.parkingType ?? 'parking'}
          {parking.operator ? ` · ${parking.operator}` : ''}
        </div>

        <div className="p-s4 bg-tint rounded-lg">
          <div className="font-serif font-medium text-[16px] tracking-tight text-ink1 mb-1">
            {parking.confidenceTier === 'verified' ? 'Add another measurement' : 'Help us verify'}
          </div>
          <p className="text-[12px] leading-tight text-ink2 font-sans mb-s3">
            Read the posted height sign at the entrance and type it in — your measurement helps
            every driver after you.
          </p>
          {canVerify ? (
            <div className="flex gap-2">
              <Button variant="secondary" className="flex-1" onClick={() => openVerify('manual')}>
                I've measured it
              </Button>
              <Button variant="secondary" className="flex-1" onClick={() => openVerify('photo')}>
                I have a photo
              </Button>
            </div>
          ) : (
            <Button variant="secondary" onClick={() => navigate('/signin')}>
              Sign in to verify
            </Button>
          )}
        </div>
      </div>

      <div className="sticky bottom-0 bg-page border-t border-rule">
        {actionError ? (
          <div className="px-s5 pt-s2 text-[11px] text-ink2 font-sans">{actionError}</div>
        ) : null}
        <div className="flex items-center gap-2 px-s5 py-s3">
          <Button
            variant="secondary"
            className="flex-1 py-3.5"
            onClick={handleTestedThis}
            loading={submittingTested}
            disabled={!firebaseUser || firebaseUser.isAnonymous}
            icon={tested ? <Check size={14} color="#2D5A3D" /> : undefined}
          >
            {tested ? 'Logged' : 'I tested this'}
          </Button>
          <Button variant="secondary" className="w-12 py-3.5 px-0" onClick={handleShare}>
            <Share size={16} />
          </Button>
          <Button
            className="flex-1 py-3.5"
            onClick={() => setNavigateOpen(true)}
            icon={<Navigate size={14} color="#FFFFFF" />}
          >
            Navigate
          </Button>
        </div>
      </div>

      <NavigateSheet
        visible={navigateOpen}
        lat={parking.lat}
        lng={parking.lng}
        label={parking.name}
        onClose={() => setNavigateOpen(false)}
      />
      <CarPickerDialog
        open={carPickerOpen}
        onClose={() => setCarPickerOpen(false)}
        onSelect={handlePickCar}
        onSelectCustom={() => setCarPickerOpen(false)}
      />
      <VerifyHeightDialog
        open={verifyOpen}
        parkingId={parkingId}
        initialTab={verifyTab}
        onClose={() => setVerifyOpen(false)}
        onSubmitted={() => queryClient.invalidateQueries({ queryKey: ['parking', parkingId] })}
      />
    </SheetShell>
  );
};

const SheetShell: React.FC<{
  children: React.ReactNode;
  onClose: () => void;
  verdictPill?: React.ReactNode;
}> = ({ children, onClose, verdictPill }) => (
  <div
    className="fixed inset-0 z-40 flex items-end md:items-stretch md:justify-end bg-black/30"
    onClick={onClose}
  >
    <div
      className="w-full md:w-[420px] md:h-full max-h-[92vh] md:max-h-none bg-page rounded-t-2xl md:rounded-none flex flex-col overflow-y-auto"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="sticky top-0 bg-page/95 backdrop-blur px-s4 py-s3 flex items-center gap-s2 border-b border-rule-soft z-10">
        <button
          onClick={onClose}
          className="w-9 h-9 rounded-pill bg-card border border-rule flex items-center justify-center"
          aria-label="Close"
        >
          <ChevronLeft size={16} />
        </button>
        <div className="flex-1" />
        {verdictPill}
      </div>
      {children}
    </div>
  </div>
);
